import type { StateCreator } from 'zustand';
import type { ItineraryState } from '../itinerary-store';

/**
 * History captures more than just `trips`. Notes, tags, and the sync-
 * manager's pending-write state all need to round-trip through undo
 * to keep the store internally consistent. Without this:
 *
 *   - Undoing a note edit silently leaves the note in `notesMap`.
 *   - Undoing the deletion of a place leaves the deleted place's note
 *     orphaned in `notesMap` (entity is gone but the note remains).
 *   - Undoing a synced edit leaves a dirty entry behind that the next
 *     flush will replay (often harmlessly, but a footgun).
 *
 * Capturing every map is more memory than the prior snapshot of just
 * `trips[]` (depth 20, each ~tens of KB at most), but bounded — capture
 * is debounced at the source (notes autosave fires every 300 ms, not per
 * keystroke).
 *
 * The set of captured fields is `SNAPSHOT_KEYS`; adding a new synced
 * field is a one-line edit (no separate snapshot/undo update).
 *
 * Undo-only: the UI surface is a pair of "Undo" toast actions after
 * destructive edits — there is no redo affordance, so no future stack.
 */
const SNAPSHOT_KEYS = [
  'trips',
  'notesMap',
  'notesMetaMap',
  'tagsMap',
  'dirtyTrips',
  'pendingDeletes',
] as const satisfies readonly (keyof ItineraryState)[];

// Derive the snapshot type directly from SNAPSHOT_KEYS so adding a key to
// the list without a companion field in ItineraryState fails typecheck,
// and so changing a field's type on ItineraryState flows through to
// snapshot/undo automatically.
export type HistorySnapshot = Pick<ItineraryState, (typeof SNAPSHOT_KEYS)[number]>;

const HISTORY_LIMIT = 20;

export interface HistorySlice {
  _past: HistorySnapshot[];
  _capture: () => void;
  undo: () => void;
}

function snapshot(s: ItineraryState): HistorySnapshot {
  const out = {} as Record<string, unknown>;
  for (const k of SNAPSHOT_KEYS) out[k] = s[k];
  return out as HistorySnapshot;
}

export const createHistorySlice: StateCreator<ItineraryState, [], [], HistorySlice> = (set, get) => ({
  _past: [],

  _capture: () => {
    const s = get();
    const snap = snapshot(s);
    set((state) => ({
      _past: [...state._past.slice(-(HISTORY_LIMIT - 1)), snap],
    }));
  },

  undo: () => {
    const s = get();
    if (s._past.length === 0) return;
    const prev = s._past[s._past.length - 1]!;
    set({
      ...prev,
      _past: s._past.slice(0, -1),
    });
  },
});
